import { CommonModule } from '@angular/common'
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core'

import { Store } from '@ngrx/store'
import { cloneDeep } from 'lodash'
import { isEmpty } from 'lodash/fp'
import { TooltipModule } from 'primeng/tooltip'

import { Point } from '@flowx/designer/modules/bpmn-editor/bpmn-graph.util'
import { FlowxCodeStore } from '@flowx/designer/modules/flowx-code/store/flowx-code.store'
// eslint-disable-next-line import/no-cycle -- pre-existing structural cycle in new-ai module; needs broader refactor to break
import { AiEventsApi } from '@flowx/designer/modules/new-ai/common/api/ai-events.api'
import { AiInitApi } from '@flowx/designer/modules/new-ai/common/api/ai-init.api'
import { DragDirective } from '@flowx/designer/modules/new-ai/common/directives/drag/drag.directive'
import {
  DragType,
  Position,
} from '@flowx/designer/modules/new-ai/common/directives/drag/drag.models'
import { AiIconsModule } from '@flowx/designer/modules/new-ai/common/icons/ai-icons.module'
import { AiActions, FileUploadConfigActions } from '@flowx/designer/modules/new-ai/store/ai.actions'
import { AiSelectors } from '@flowx/designer/modules/new-ai/store/ai.selectors'

/** Extra `right` offset applied when the FlowX Code panel is taking screen space
 * (collapsed strip), so the FAB doesn't sit on top of it. */
const FLOWX_CODE_RIGHT_OFFSET_PX = 50

@Component({
  selector: 'flx-ai-fab',
  templateUrl: './fab.component.html',
  styleUrls: ['./fab.component.scss'],
  host: {
    class: 'fixed z-[1005]',
  },
  imports: [CommonModule, AiIconsModule, DragDirective, TooltipModule],
})
export class AiFabComponent implements OnInit, OnDestroy {
  #aiInitApi = inject(AiInitApi)
  #aiEventsApi = inject(AiEventsApi)
  #store = inject(Store)
  #flowxCodeStore = inject(FlowxCodeStore)

  #fabPositionState = computed(() => {
    const fabPosition = this.#store.selectSignal(AiSelectors.fabPosition)()
    return {
      position: signal(fabPosition),
    }
  })
  #isFabMoved = false
  #sseConnection = this.#aiEventsApi.initSseConnection()

  #isZenMode = this.#store.selectSignal(AiSelectors.isZenMode)
  /** The FAB used to also auto-hide when FlowX Code was open + expanded
   * (rationale: avoid visual clutter when the new assistant has the
   * user's attention). Dropped 2026-06-18 — the user reported the FAB
   * was "invisible" because they were in that state and couldn't see
   * it. The FAB is the universal entry point for the AI Command Center
   * (the OLD `new-ai` window); FlowX Code has its own keyboard
   * shortcuts (Cmd+Shift+L, Cmd+K). The two coexist; users pick the
   * one they want. Zen mode is still the only forced-hide trigger. */
  hideFab = computed(() => this.#isZenMode())
  showFloatingWindow = this.#store.selectSignal(AiSelectors.isExpanded)
  fabStyle = computed(() => {
    const position = this.#fabPositionState().position()
    // While FlowX Code shows its 40px strip on the right, push the FAB further
    // left so it never overlaps the strip.
    const shiftRight = this.#flowxCodeStore.collapsed() ? FLOWX_CODE_RIGHT_OFFSET_PX : 0
    return this.#convertPositionToNgStyle(position, shiftRight)
  })

  readonly DragType = DragType
  readonly moveTolerance = 2
  readonly dragPadding = { top: 80, right: 24, bottom: 80, left: 65 }

  showWindow(): void {
    if (!this.#isFabMoved) {
      this.#store.dispatch(AiActions.fabClick())
    }
  }

  onDragMove(event: { to: Position; delta: Point }): void {
    const { to, delta } = event

    if (delta) {
      this.#setFabMoved(delta)
      this.#fabPositionState().position.set(to)
    }
  }

  onDragEnd(event: { to: Position; delta: Point }): void {
    const { to, delta } = event

    if (delta) {
      this.#setFabMoved(delta)
      this.#store.dispatch(AiActions.fabMoved({ position: to }))
    }
  }

  ngOnInit(): void {
    if (isEmpty(this.#store.selectSignal(AiSelectors.fileUploadConfig)())) {
      this.#aiInitApi.init().subscribe((fileUploadConfig) => {
        this.#store.dispatch(
          FileUploadConfigActions.loadFileConfigSuccess({
            fileUploadConfig: cloneDeep(fileUploadConfig),
          })
        )
      })
    }
  }

  ngOnDestroy(): void {
    this.#sseConnection?.close()
  }

  #convertPositionToNgStyle(position: Position, rightShift = 0): Record<string, string> {
    return Object.entries(position).reduce(
      (style, [key, value]) => {
        const px = key === 'right' ? Number(value) + rightShift : value
        style[key] = `${px}px`
        return style
      },
      {} as Record<string, string>
    )
  }

  #setFabMoved(delta: Point): void {
    this.#isFabMoved = this.#getIsFabMoved(delta)
  }

  #getIsFabMoved(delta: Point): boolean {
    return Math.abs(delta.x) > this.moveTolerance || Math.abs(delta.y) > this.moveTolerance
  }
}
